// Client-PDF import → NATIVE editable album (manual order).
//
// Instead of embedding the client's flattened PDF (which can't be edited), we
// rasterize it into our OWN model so the imported order behaves EXACTLY like a
// normal one — editable in the editor, full status pipeline, 3D preview, and
// print-ready downloads — by reusing every existing pipeline:
//
//   • Interior pages  → one JPEG per page → a `photos` row each → `pages` JSONB
//                       as 1-up-per-page (the 1-up slot is the full page, so the
//                       client page reproduces edge-to-edge, no borders). A page
//                       not shaped like our trim is stored at its OWN shape —
//                       never letterboxed with a zoomed copy of itself (#1247,
//                       pdfRaster.planInteriorFit) — and a bleed the client's
//                       tool painted is cut first (lib/edgeBandTrim).
//   • Cover (A/B/C)   → front/back JPEGs → cover_customizations.blankCoverImage
//                       (full-bleed front) + backCoverImages[0] (back). cover_id
//                       stays null (blank), so OUR locked geometry, OUR logo on
//                       the back, and NO text overlay are applied by the cover
//                       composer; the client's own title art is untouched.
//   • Render          → renderAndUploadAlbumPdfs (the normal path) builds the
//                       cover + interior from the now-native session, adds our
//                       intro/outro + ÷4 padding, finalizes, AND generates the
//                       3D flipbook preview.
//
// Runs in the admin's browser (rasterize + upload). No new cover composer, no
// new interior pipeline, no new editor.

import { sessionsApi, photosApi, ordersApi, uploadToSignedUrl } from './api';
import type { Cover, AlbumSession } from './api';
import { renderAndUploadAlbumPdfs } from './multiAlbumPdf';
import { pdfPageCount, pdfPageSizeMm, composeInteriorPageImage, composeCoverWrapImage, type RasterPage } from './pdfRaster';
import { DEFAULT_PRINT_SETTINGS, withDerivedVinyl, type PrintSettings } from './assemblyTemplate';

export type CoverCase = 'A' | 'B' | 'C';

export interface ImportCoverSpec {
  /** A = full wrap (front+spine+back in one page), B = separate front+back
   *  pages, C = front A4 page alone. */
  kase: CoverCase;
  bytes: Uint8Array;
  /** B / C — 0-based page index of the front. */
  frontIndex: number;
  /** B — 0-based page index of the back (-1 = none / not provided). */
  backIndex: number;
  /** A — where the back region ends / the front region begins (fractions of the
   *  wrap width). Defaults to a symmetric split if omitted. */
  wrapCut?: { backRight: number; frontLeft: number };
}

export interface ImportInteriorSpec {
  bytes: Uint8Array;
}

export interface ImportSettings {
  logo_url?: string | null;
  intro_page_1_url?: string | null;
  outro_page_2_url?: string | null;
  pdf_bleed_mm?: number | null;
  /** Cover geometry (spine width / hinge gap) — used to composite the client
   *  cover into our exact sheet layout. Falls back to DEFAULT_PRINT_SETTINGS. */
  print_template_settings?: Partial<PrintSettings> | null;
}

export interface ImportNativeAlbumOpts {
  shopifyOrderId: string;
  albumIndex: number;
  albumCount: number;
  destination?: string | null;
  year?: string | null;
  cover?: ImportCoverSpec | null;
  interior?: ImportInteriorSpec | null;
  covers: Cover[];
  settings: ImportSettings;
  onProgress?: (message: string) => void;
}

export interface ImportNativeAlbumResult {
  /** true when the print PDFs (+ 3D preview) were generated. Only happens when
   *  the album ends up with interior pages — a cover-only import saves an
   *  editable cover but defers the print render to the first regen. */
  rendered: boolean;
  pageCount: number;
}

async function uploadOneInteriorPhoto(
  sessionId: string,
  page: RasterPage,
  index: number,
): Promise<string> {
  const name = `imported-page-${index + 1}.jpg`;
  const signed = await photosApi.getUploadUrl(sessionId, name, 'image/jpeg');
  if (!signed.upload_url || !signed.photo_id) throw new Error('Réponse upload photo invalide');
  await uploadToSignedUrl(signed.upload_url, new File([page.blob], name, { type: 'image/jpeg' }), 'image/jpeg');
  // Minimal metadata so the photo is a first-class, renderable row. No faces /
  // EXIF — these are finished pages, not camera photos.
  try {
    await photosApi.updateMetadata(signed.photo_id, {
      face_count: 0, face_data: [], exif_date: null, exif_gps_lat: null, exif_gps_lng: null,
      is_blurry: false, width: page.width, height: page.height,
      file_size_kb: Math.round(page.blob.size / 1024),
    });
  } catch { /* row still exists with the original bytes; metadata is best-effort */ }
  return signed.photo_id;
}

async function uploadCoverImage(
  sessionId: string,
  page: RasterPage,
  which: 'front' | 'back',
): Promise<string> {
  const filename = `imported-cover-${which}.jpg`;
  // Front → blank-cover/ prefix (full-bleed front image); back → back-cover/.
  const signed = which === 'front'
    ? await photosApi.getBlankCoverUploadUrl(sessionId, filename, 'image/jpeg')
    : await photosApi.getBackCoverUploadUrl(sessionId, filename, 'image/jpeg');
  await uploadToSignedUrl(
    signed.upload_url,
    new File([page.blob], filename, { type: 'image/jpeg' }),
    'image/jpeg',
  );
  return signed.storage_path;
}

/**
 * Every page of a client PDF → one print-grade photo row of this album, in
 * page order (client bleed cut, landscape turned; trim-shaped pages laid on
 * our 200×297 + bleed sheet whole, any other shape kept as itself — see
 * pdfRaster.planInteriorFit). `width`/`height` are the stored file's own.
 * Shared by the New Order import and « Importer un PDF » on an existing album
 * (owner, 2026-10-17). One page at a time: low peak memory.
 */
export async function convertPdfPagesToPhotos(
  sessionId: string,
  bytes: Uint8Array,
  bleedMm: number,
  progress: (message: string) => void = () => {},
): Promise<string[]> {
  const count = await pdfPageCount(bytes);
  const photoIds: string[] = [];
  for (let i = 0; i < count; i++) {
    progress(`Conversion page ${i + 1}/${count}…`);
    const page = await composeInteriorPageImage(bytes, i, { trimWmm: 200, trimHmm: 297, bleedMm });
    photoIds.push(await uploadOneInteriorPhoto(sessionId, page, i));
  }
  return photoIds;
}

export async function importNativeAlbum(opts: ImportNativeAlbumOpts): Promise<ImportNativeAlbumResult> {
  const { shopifyOrderId, albumIndex, covers, settings, onProgress } = opts;
  const progress = onProgress ?? (() => {});

  // 1. Ensure the album session exists; we need its id for photo + cover uploads.
  progress('Préparation de l’album…');
  const { session } = await sessionsApi.upsert(shopifyOrderId, albumIndex, {
    destination: opts.destination ?? null,
    year: opts.year ?? null,
    status: 'draft',
  });
  const sessionId = session?.id;
  if (!sessionId) throw new Error('Session d’album introuvable après création.');

  // Accumulate everything into one final upsert so we don't race two writes.
  const finalPatch: Partial<AlbumSession> = {};
  let pageCount = 0;

  // 2. Interior → each client page becomes one photo (convertPdfPagesToPhotos:
  //    client bleed cut, landscape turned, never letterboxed with a zoomed
  //    copy of itself), then one 1-up page each. Compose+upload one at a
  //    time to keep peak memory low.
  if (opts.interior && opts.interior.bytes.length > 0) {
    progress('Conversion de l’intérieur…');
    const photoIds = await convertPdfPagesToPhotos(sessionId, opts.interior.bytes, opts.settings.pdf_bleed_mm ?? 3, progress);
    finalPatch.pages = {
      pages: photoIds.map((id, i) => ({ page: i + 1, layout: '1-up', photos: [id] })),
      unassigned_photos: [],
    };
    finalPatch.page_count = photoIds.length;
    pageCount = photoIds.length;
  }

  // 3. Cover → re-fit the client's finished cover into OUR sheet layout as ONE
  //    continuous full-wrap image (front→front panel, spine→spine column,
  //    back→back panel). cover_id stays null so the generator adds OUR geometry,
  //    OUR logo on the back, and the marry-label — with no text overlay.
  if (opts.cover && opts.cover.bytes.length > 0) {
    progress('Conversion de la couverture…');
    const ps: PrintSettings = withDerivedVinyl({ ...DEFAULT_PRINT_SETTINGS, ...(opts.settings.print_template_settings ?? {}) });
    const spineColMm = ps.spineWmm + 2 * ps.jointGapMm;
    // If the client's full-wrap cover is ALREADY at our sheet size (±6mm), keep
    // it EXACTLY (pass-through, no slicing) — only our logo + order-id get added.
    // Otherwise re-fit it into our geometry.
    let passthrough = false;
    if (opts.cover.kase === 'A') {
      try {
        const sz = await pdfPageSizeMm(opts.cover.bytes, opts.cover.frontIndex >= 0 ? opts.cover.frontIndex : 0);
        const TOL = 6;
        passthrough = Math.abs(sz.wmm - ps.sheetWmm) <= TOL && Math.abs(sz.hmm - ps.sheetHmm) <= TOL;
        progress(passthrough
          ? 'Couverture déjà au bon format — conservée telle quelle…'
          : 'Couverture refit dans nos specs…');
      } catch { /* size unknown → refit */ }
    }
    const wrap = await composeCoverWrapImage({
      kase: opts.cover.kase,
      bytes: opts.cover.bytes,
      frontIndex: opts.cover.frontIndex,
      backIndex: opts.cover.backIndex,
      wrapCut: opts.cover.wrapCut ?? { backRight: 0.485, frontLeft: 0.515 },
      sheetWmm: ps.sheetWmm,
      sheetHmm: ps.sheetHmm,
      spineColMm,
      passthrough,
      longEdgePx: passthrough ? 6000 : undefined,
    });
    const wrapPath = await uploadCoverImage(sessionId, wrap, 'front');
    finalPatch.cover_id = null;
    finalPatch.cover_customizations = {
      coverWrapImage: { storage_path: wrapPath },
      backCoverBgColor: null,
      frontCoverBgColor: null,
      hideFrontCoverImage: false,
    };
  }

  // 4. Persist the native model in one write.
  if (Object.keys(finalPatch).length > 0) {
    progress('Enregistrement…');
    await sessionsApi.upsert(shopifyOrderId, albumIndex, finalPatch);
  }

  // 5. Render the print PDFs + 3D preview through the NORMAL pipeline — only
  //    possible once the album has interior pages (buildAlbumPdfInput needs
  //    pages). Cover-only imports stay editable and render on the first regen.
  if (pageCount > 0) {
    progress('Génération des PDF (couverture + intérieur)…');
    const ok = await renderAndUploadAlbumPdfs({
      shopifyOrderId,
      albumIndex,
      covers,
      logoUrl: settings.logo_url ?? null,
      introPageUrl: settings.intro_page_1_url ?? null,
      outroPageUrl: settings.outro_page_2_url ?? null,
      bleedMm: settings.pdf_bleed_mm ?? 3,
      force: true,
      onProgress: (p) => { if (p) progress(p.message); },
    });

    // 6. Reconcile the ORDER row with what was actually imported.
    //    /orders/manual stored the page count the admin TYPED; this album now
    //    holds the real one. Without this, orders.page_count keeps the guess —
    //    which drives the spine pill and the extra-page surcharge. The worker
    //    reuses recomputeAndPersistOrderPricing + computeAlbumCost; nothing is
    //    re-implemented here. Non-fatal: the pages are already safely stored.
    progress('Recalcul des pages et du prix…');
    try {
      await ordersApi.importReprice(shopifyOrderId);
    } catch (e) {
      console.warn('[importNativeAlbum] reprice failed (non-fatal):', e);
    }

    return { rendered: ok, pageCount };
  }

  return { rendered: false, pageCount };
}
